import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AuthCallback component handles the OAuth redirect from Emergent Auth.
 * It extracts the session_id from the URL fragment, exchanges it for a JWT,
 * and redirects to the dashboard.
 * 
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const AuthCallback = ({ onLogin }) => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in React StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processOAuthCallback = async () => {
      try {
        // Extract session_id from URL fragment (comes after #)
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.replace("#", ""));
        const sessionId = params.get("session_id");

        if (!sessionId) {
          console.error("[AuthCallback] No session_id found in URL fragment");
          toast.error("Authentication failed - no session ID");
          navigate("/");
          return;
        }

        console.log("[AuthCallback] Processing session_id...");

        // Exchange session_id for JWT token via our backend
        const response = await axios.post(`${API}/auth/google/session`, {
          session_id: sessionId
        });

        const { token, user } = response.data;

        if (!token || !user) {
          throw new Error("Invalid response from server");
        }

        console.log("[AuthCallback] OAuth successful, user:", user.email);

        // Store token and update app state
        localStorage.setItem("token", token);
        
        // Call onLogin to update parent App state
        if (onLogin) {
          onLogin(token, user);
        }

        toast.success(`Welcome${user.name ? `, ${user.name}` : ""}!`);
        
        // Clear the URL fragment and redirect to dashboard
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/dashboard", { replace: true });

      } catch (error) {
        console.error("[AuthCallback] OAuth error:", error);
        const errorMessage = error.response?.data?.detail || "Authentication failed";
        toast.error(errorMessage);
        navigate("/");
      }
    };

    processOAuthCallback();
  }, [navigate, onLogin]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-stone-100">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-4 border-amber-900 border-t-transparent rounded-full animate-spin" />
        <p className="text-lg font-medium text-stone-700">Completing sign in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
